import {
  Avatar,
  Button,
  Checkbox,
  Input,
  Modal,
  Select,
  Tag,
  Typography,
  Upload,
} from "antd";
import {
  CalendarOutlined,
  CarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  MailOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Employee, EmployeeTag } from "./types";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import SignatureSection from "./SignatureSection";

const { TextArea } = Input;

interface EmployeeEditModalProps {
  employee: Employee | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (employee: Employee) => void;
  onResign?: (employee: Employee) => void;
  onReinstate?: (employee: Employee) => void;
  allTags?: EmployeeTag[];
  onTagChange?: (employeeId: string, newTags: EmployeeTag[]) => void;
  onNewTagCreated?: () => void;
}

function EmployeeEditModal({
  employee,
  open,
  onOpenChange,
  onSave,
  onResign,
  onReinstate,
  allTags = [],
  onTagChange,
  onNewTagCreated,
}: EmployeeEditModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<Employee>>({});
  const [profileImg, setProfileImg] = useState("");
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState(false);
  const [localTags, setLocalTags] = useState<EmployeeTag[]>([]);
  const [newTagName, setNewTagName] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);
  const isResigned = employee?.status === "resigned";

  useEffect(() => {
    if (employee) {
      setFormData(employee);
      setProfileImg(employee.avatar || "");
      setProfileUrl(null);
      setLocalTags(employee.tags ?? []);
    }
  }, [employee]);

  const handleAddTag = async (tagId: string) => {
    if (!employee?.id) return;
    const tag = allTags.find((t) => t.id === tagId);
    if (!tag) return;
    try {
      await orpcClient.employeeTag.addEmployee({ employeeTagId: tagId, employeeId: [employee.id] });
      const next = [...localTags, tag];
      setLocalTags(next);
      onTagChange?.(employee.id, next);
    } catch (error) {
      console.error("employeeTag.addEmployee_ERROR", error);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!employee?.id) return;
    try {
      await orpcClient.employeeTag.removeEmployee({ employeeTagId: tagId, employeeId: [employee.id] });
      const next = localTags.filter((t) => t.id !== tagId);
      setLocalTags(next);
      onTagChange?.(employee.id, next);
    } catch (error) {
      console.error("employeeTag.removeEmployee_ERROR", error);
    }
  };

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name || !employee?.id) return;
    setCreatingTag(true);
    try {
      const created = await single(orpcClient.employeeTag.create([{ name }]));
      if (!created) return;
      await orpcClient.employeeTag.addEmployee({ employeeTagId: created.id, employeeId: [employee.id] });
      const next = [...localTags, { id: created.id, name: created.name }];
      setLocalTags(next);
      setNewTagName("");
      onTagChange?.(employee.id, next);
      onNewTagCreated?.();
    } catch (error) {
      console.error("employeeTag.create_ERROR", error);
    } finally {
      setCreatingTag(false);
    }
  };

  const handleBeforeUpload = async (file: File) => {
    if (uploadState) return false;
    setUploadState(true);
    try {
      const { blobSasUrl, blobUrl } = await orpcClient.storage.createBlobSasUrl(
        {
          fileName: file.name,
          contentType: file.type,
        },
      );
      await fetch(blobSasUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type, "x-ms-blob-type": "BlockBlob" },
      });
      setProfileImg(URL.createObjectURL(file));
      setProfileUrl(blobUrl);
    } catch (error) {
      console.error("upload error", error);
    } finally {
      setUploadState(false);
    }
    return false;
  };

  const handleSubmit = () => {
    if (formData.id) {
      const avatar = profileUrl ?? formData.avatar;
      onSave({ ...formData, ...(avatar ? { avatar } : {}), tags: localTags } as Employee);
      onOpenChange(false);
    }
  };

  const updateField = (field: keyof Employee, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const sectionTitleStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 500,
    color: "#999",
    marginBottom: 12,
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 4,
  };

  return (
    <Modal
      open={open}
      onCancel={() => onOpenChange(false)}
      title={employee?.name ? t("employee.edit.editTitle", { name: employee.name }) : t("employee.edit.title")}
      width={700}
      footer={
        isResigned ? (
          onReinstate && employee ? (
            <Button
              key="reinstate"
              type="primary"
              onClick={() => onReinstate(employee)}
            >
              {t("employee.edit.reinstate")}
            </Button>
          ) : null
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {onResign && (
              <Button
                key="resign"
                danger
                onClick={() => {
                  Modal.confirm({
                    title: t("employee.edit.confirmResignTitle"),
                    content: (
                      <Typography.Text>
                        {t("employee.edit.confirmResignContent", { name: employee?.name })}
                      </Typography.Text>
                    ),
                    okText: t("employee.edit.confirmResignOk"),
                    cancelText: t("employee.edit.cancel"),
                    okButtonProps: { danger: true },
                    onOk: () => {
                      if (employee) {
                        onResign?.(employee);
                      }
                    },
                  });
                }}
              >
                {t("employee.edit.resign")}
              </Button>
            )}
            <Button key="save" type="primary" onClick={handleSubmit}>
              {t("employee.edit.saveChanges")}
            </Button>
          </div>
        )
      }
      styles={{
        body: { maxHeight: "60vh", overflowY: "auto", paddingRight: 8 },
      }}
    >
      {isResigned && (
        <div
          style={{
            background: "#fff1f0",
            border: "1px solid #ffa39e",
            borderRadius: 6,
            padding: "8px 12px",
            marginBottom: 16,
            color: "#cf1322",
            fontSize: 13,
          }}
        >
          {t("employee.edit.resignedReadOnly")}
        </div>
      )}

      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}
      >
        <Upload
          name="avatar"
          listType="picture-card"
          showUploadList={false}
          beforeUpload={isResigned ? () => false : handleBeforeUpload}
          disabled={isResigned}
        >
          {profileImg ? (
            <img
              src={profileImg}
              alt="avatar"
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          ) : (
            <Avatar size={80} icon={<UserOutlined />} />
          )}
        </Upload>
      </div>

      <p style={{ color: "#999", fontSize: 13, marginBottom: 24 }}>
        {t("employee.edit.subtitle")}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div>
          <div style={sectionTitleStyle}>
            <CalendarOutlined />
            <span>{t("employee.edit.basicData")}</span>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{t("employee.edit.name")}</label>
            <Input
              disabled={isResigned}
              value={formData.name || ""}
              onChange={(e) => updateField("name", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label style={labelStyle}>{t("employee.edit.birthday")}</label>
              <Input
                type="date"
                disabled={isResigned}
                value={formData.birthday || ""}
                onChange={(e) => updateField("birthday", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.bloodType")}</label>
              <Select
                disabled={isResigned}
                value={formData.bloodType || undefined}
                onChange={(value) => updateField("bloodType", value)}
                placeholder={t("employee.edit.bloodTypePlaceholder")}
                style={{ width: "100%" }}
                options={[
                  { value: "A", label: t("employee.edit.bloodTypeA") },
                  { value: "B", label: t("employee.edit.bloodTypeB") },
                  { value: "AB", label: t("employee.edit.bloodTypeAB") },
                  { value: "O", label: t("employee.edit.bloodTypeO") },
                ]}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.idNumber")}</label>
              <Input
                disabled={isResigned}
                value={formData.idNumber || ""}
                onChange={(e) => updateField("idNumber", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <CarOutlined />
            <span>{t("employee.edit.licenses")}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Checkbox
                disabled={isResigned}
                checked={formData.hasMotorcycleLicense || false}
                onChange={(e) =>
                  updateField("hasMotorcycleLicense", e.target.checked)
                }
              >
                {t("employee.edit.motorcycle")}
              </Checkbox>
              <span style={{ fontSize: 13, color: "#999" }}>
                {formData.hasMotorcycleLicense ? t("employee.edit.has") : t("employee.edit.hasNot")}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Checkbox
                disabled={isResigned}
                checked={formData.hasCarLicense || false}
                onChange={(e) => updateField("hasCarLicense", e.target.checked)}
              >
                {t("employee.edit.car")}
              </Checkbox>
              <span style={{ fontSize: 13, color: "#999" }}>
                {formData.hasCarLicense ? t("employee.edit.has") : t("employee.edit.hasNot")}
              </span>
            </div>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <EnvironmentOutlined />
            <span>{t("employee.edit.contactAddress")}</span>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label style={labelStyle}>{t("employee.edit.city")}</label>
              <Input
                disabled={isResigned}
                value={formData.city || ""}
                onChange={(e) => updateField("city", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.district")}</label>
              <Input
                disabled={isResigned}
                value={formData.district || ""}
                onChange={(e) => updateField("district", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.postalCode")}</label>
              <Input
                disabled={isResigned}
                value={formData.postalCode || ""}
                onChange={(e) => updateField("postalCode", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.address")}</label>
              <Input
                disabled={isResigned}
                value={formData.address || ""}
                onChange={(e) => updateField("address", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <MailOutlined />
            <span>{t("employee.edit.contactInfo")}</span>
          </div>
          <div>
            <label style={labelStyle}>{t("employee.email")}</label>
            <Input
              type="email"
              disabled={isResigned}
              value={formData.email || ""}
              onChange={(e) => updateField("email", e.target.value)}
            />
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <TeamOutlined />
            <span>{t("employee.edit.emergencyContact")}</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label style={labelStyle}>{t("employee.edit.emergencyContact")}</label>
              <Input
                disabled={isResigned}
                value={formData.emergencyContact || ""}
                onChange={(e) =>
                  updateField("emergencyContact", e.target.value)
                }
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.relationship")}</label>
              <Input
                disabled={isResigned}
                value={formData.emergencyRelation || ""}
                onChange={(e) =>
                  updateField("emergencyRelation", e.target.value)
                }
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.emergencyPhone")}</label>
              <Input
                disabled={isResigned}
                value={formData.emergencyPhone || ""}
                onChange={(e) => updateField("emergencyPhone", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <FileTextOutlined />
            <span>{t("employee.edit.notes")}</span>
          </div>
          <TextArea
            disabled={isResigned}
            placeholder={t("employee.edit.notesPlaceholder")}
            value={formData.notes || ""}
            onChange={(e) => updateField("notes", e.target.value)}
            rows={3}
          />
        </div>

        <SignatureSection
          employeeId={employee?.id}
          sectionTitleStyle={sectionTitleStyle}
          disabled={isResigned}
        />

        <div>
          <div style={sectionTitleStyle}>
            <TagsOutlined />
            <span>{t("employee.edit.tags")}</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            {localTags.map((tag) => (
              <Tag
                key={tag.id}
                closable={!isResigned}
                onClose={() => handleRemoveTag(tag.id)}
                color="blue"
              >
                {tag.name}
              </Tag>
            ))}
          </div>
          {!isResigned && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {allTags.filter((t) => !localTags.some((lt) => lt.id === t.id)).length > 0 && (
                <Select
                  placeholder={t("employee.edit.selectExistingTag")}
                  style={{ minWidth: 160 }}
                  value={undefined}
                  onChange={handleAddTag}
                  options={allTags
                    .filter((t) => !localTags.some((lt) => lt.id === t.id))
                    .map((t) => ({ label: t.name, value: t.id }))}
                />
              )}
              <Input
                placeholder={t("employee.edit.newTagPlaceholder")}
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onPressEnter={handleCreateTag}
                style={{ width: 160 }}
              />
              <Button loading={creatingTag} onClick={handleCreateTag} disabled={!newTagName.trim()}>
                {t("employee.edit.createTag")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default EmployeeEditModal;
